import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardService } from '@/services/dashboard';
import { formatCurrency } from '@/lib/utils';
import {
  Users,
  CalendarCheck,
  CurrencyDollar,
  Bed,
  TrendUp,
  TrendDown,
  Clock,
} from '@phosphor-icons/react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const KPICard = ({ title, value, change, icon: Icon, trend, index = 0 }) => {
  const isPositive = change >= 0;
  const TrendIcon = isPositive ? TrendUp : TrendDown;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
    >
      <Card className="hover-lift bg-card" data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <Icon className="h-5 w-5 text-muted-foreground" weight="duotone" />
        </CardHeader>
        <CardContent>
          <motion.div 
            className="text-2xl md:text-3xl font-medium tracking-tight"
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.1 + 0.2, type: 'spring', stiffness: 200 }}
          >
            {value}
          </motion.div>
          {change !== undefined && (
            <div className={cn('flex items-center gap-1 text-xs mt-2', isPositive ? 'text-success' : 'text-destructive')}>
              <TrendIcon className="h-3 w-3" weight="bold" />
              <span className="font-medium">{Math.abs(change)}%</span>
              <span className="text-muted-foreground">vs last month</span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ActivityItem = ({ activity }) => {
  const typeColors = {
    appointment: 'bg-primary/10 text-primary',
    admission: 'bg-secondary/10 text-secondary',
    billing: 'bg-success/10 text-success',
    lab: 'bg-warning/10 text-warning',
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-0" data-testid="activity-item">
      <div className={cn('p-2 rounded-md', typeColors[activity.type])}>
        <Clock className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{activity.message}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{activity.patient}</p>
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{activity.time}</span>
    </div>
  );
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartColors, setChartColors] = useState({
    primary: '#0EA5A4',
    secondary: '#2563EB',
    border: 'rgba(0,0,0,0.1)',
    text: '#64748b',
  });

  useEffect(() => {
    // Get computed colors from CSS variables
    const computeColors = () => {
      if (typeof window !== 'undefined') {
        const style = getComputedStyle(document.documentElement);
        const primary = style.getPropertyValue('--primary');
        const secondary = style.getPropertyValue('--secondary');
        const border = style.getPropertyValue('--border');
        const mutedForeground = style.getPropertyValue('--muted-foreground');
        
        setChartColors({
          primary: primary ? `hsl(${primary})` : '#0EA5A4',
          secondary: secondary ? `hsl(${secondary})` : '#2563EB',
          border: border ? `hsl(${border})` : 'rgba(0,0,0,0.1)',
          text: mutedForeground ? `hsl(${mutedForeground})` : '#64748b',
        });
      }
    };
    
    computeColors();
    // Recompute on theme change
    const observer = new MutationObserver(computeColors);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, patientRes, revenueRes, activityRes] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getPatientInflow(),
          dashboardService.getRevenueTrend(),
          dashboardService.getActivities(),
        ]);

        setStats(statsRes.data);
        setPatientData(patientRes.data);
        setRevenueData(revenueRes.data);
        setActivities(activityRes.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Dashboard</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Overview of your hospital operations
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const patientChartData = {
    labels: patientData?.map((d) => d.month) || [],
    datasets: [
      {
        label: 'Patient Inflow',
        data: patientData?.map((d) => d.patients) || [],
        borderColor: chartColors.primary,
        backgroundColor: 'rgba(14, 165, 164, 0.15)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: chartColors.primary,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const revenueChartData = {
    labels: revenueData?.map((d) => d.month) || [],
    datasets: [
      {
        label: 'Revenue',
        data: revenueData?.map((d) => d.revenue) || [],
        backgroundColor: chartColors.secondary,
        borderRadius: 6,
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#fff',
        titleColor: '#0f172a',
        bodyColor: '#0f172a',
        borderColor: chartColors.border,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: chartColors.border,
          drawBorder: false,
        },
        ticks: {
          color: chartColors.text,
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: chartColors.text,
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Dashboard</h1>
        <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
          Overview of your hospital operations
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          index={0}
          title="Total Patients"
          value={stats?.totalPatients?.toLocaleString()}
          change={stats?.patientsChange}
          icon={Users}
        />
        <KPICard
          index={1}
          title="Appointments Today"
          value={stats?.appointmentsToday}
          change={stats?.appointmentsChange}
          icon={CalendarCheck}
        />
        <KPICard
          index={2}
          title="Revenue (Monthly)"
          value={formatCurrency(stats?.revenue)}
          change={stats?.revenueChange}
          icon={CurrencyDollar}
        />
        <KPICard
          index={3}
          title="Bed Occupancy"
          value={`${stats?.bedOccupancy}%`}
          change={stats?.occupancyChange}
          icon={Bed}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <motion.div 
          className="md:col-span-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-medium tracking-tight">Patient Inflow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <Line key={chartColors.primary} data={patientChartData} options={chartOptions} />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-2xl md:text-3xl font-medium tracking-tight">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {activities.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.05 }}
                  >
                    <ActivityItem activity={activity} />
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-2xl md:text-3xl font-medium tracking-tight">Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <Bar key={chartColors.secondary} data={revenueChartData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}